import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { EventItem, Place } from '../../types/shared/event';
import { BookIcon, WalkIcon, BikeIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { logger } from '../../lib/logger';
import { calculateAllTravelTimes } from '../../utils/travelTimeCalculator';
import { favoritesService } from '../../lib/favorites/favoritesService';
import { useSection } from '../../hooks/useSection';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime, formatTimeRange } from '../../utils/formatDuration';
import { ticketmasterEventAdapter } from '../../lib/adapters/ticketmasterEventAdapter';
import { useImageDisplay } from '../../hooks/useImageDisplay';

interface DetailCardProps {
  item: EventItem;
  items: EventItem[];
  onClose: () => void;
  onItemChange: (item: EventItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: Place | null;
  context?: {
    translationTableId?: string;
    locale?: string;
  };
  onShowPlace?: (place: Place) => void;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors,
  behavior = 'default',
  showIndex,
  place,
  context,
  onShowPlace
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const [isFavorite, setIsFavorite] = useState(() => {
    const favoriteId = item.source === 'ticketmaster' ? `ticketmaster_${item.eventId}` : item.id;
    return favoritesService.isFavorite(favoriteId);
  });

  // Use the useImageDisplay hook to determine if image should be displayed
  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  // Calculate travel times based on distance
  const travelTimes = React.useMemo(() => {
    if (typeof item.distance === 'number' && !isNaN(item.distance)) {
      return calculateAllTravelTimes(item.distance);
    }
    return { walkTime: 0, bikeTime: 0, driveTime: 0 };
  }, [item.distance]);

  // Utiliser le hook useAutoTranslation avec le sectionType
  const { translate, translating, error: translationError } = useAutoTranslation({
    description: item.description,
    currentLanguage: i18n.language,
    context: {
      tableId: context?.translationTableId || tableId,
      itemId: item.eventId || item.id,
      sectionType: 'event-api',
      source_hash: item.source === 'ticketmaster' ? item.description : undefined
    }
  });

  // Check if category and type need translation
  useEffect(() => {
    const checkCategoryAndTypeTranslations = async () => {
      if (!context?.locale || item.source !== 'ticketmaster') return;
      
      try {
        // Check if category exists in translations
        if (item.category && !translations[item.category]) {
          logger.debug('Vérification de la catégorie dans les traductions', {
            category: 'Translation',
            data: { 
              terme: item.category,
              sourceLanguage: context.locale,
              existe_dans_translations: !!translations[item.category],
              translations_keys: Object.keys(translations).slice(0, 10)
            }
          });
          
          // Translate category
          const categoryTranslations = await ticketmasterEventAdapter.translateCategoryOrType(
            item.category,
            context.locale as any
          );
          
          // Add to translations object
          if (categoryTranslations) {
            Object.keys(categoryTranslations).forEach(lang => {
              if (!translations[item.category]) {
                translations[item.category] = {};
              }
              translations[item.category][lang] = categoryTranslations[lang];
            });
            
            logger.debug('Traductions de catégorie ajoutées', {
              category: 'Translation',
              data: { 
                terme: item.category,
                traductions: categoryTranslations
              }
            });
          }
        }
        
        // Check if type exists in translations
        if (item.type && !translations[item.type]) {
          logger.debug('Vérification du type dans les traductions', {
            category: 'Translation',
            data: { 
              terme: item.type,
              sourceLanguage: context.locale,
              existe_dans_translations: !!translations[item.type],
              translations_keys: Object.keys(translations).slice(0, 10)
            }
          });
          
          // Translate type
          const typeTranslations = await ticketmasterEventAdapter.translateCategoryOrType(
            item.type,
            context.locale as any
          );
          
          // Add to translations object
          if (typeTranslations) {
            Object.keys(typeTranslations).forEach(lang => {
              if (!translations[item.type]) {
                translations[item.type] = {};
              }
              translations[item.type][lang] = typeTranslations[lang];
            });
            
            logger.debug('Traductions de type ajoutées', {
              category: 'Translation',
              data: { 
                terme: item.type,
                traductions: typeTranslations
              }
            });
          }
        }
      } catch (error) {
        logger.error('Error translating category or type', {
          category: 'Translation',
          data: { error, item }
        });
      }
    };
    
    checkCategoryAndTypeTranslations();
  }, [item, translations, context?.locale]);

  // Trouver les autres dates pour le même spectacle
  const otherDates = React.useMemo(() => {
    if (!item.showId) return [];
    return items
      .filter(event => 
        event.showId === item.showId && 
        event.id !== item.id
      )
      .sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        return dateCompare !== 0 ? 
          dateCompare : 
          a.hour.localeCompare(b.hour);
      });
  }, [items, item]);

  // Effectuer la traduction quand la langue change
  useEffect(() => {
    const checkAndTranslate = async () => {
      const currentLang = i18n.language;
      
      // Vérifier si la traduction est manquante ou vide
      const currentDescription = item.descriptions[currentLang];
      const isDescriptionMissing = !currentDescription || 
                               currentDescription.trim() === '' || 
                               currentDescription === '\n';

      if (isDescriptionMissing) {
        logger.debug('Translation needed', {
          category: 'Translation',
          data: { 
            itemId: item.id,
            itemName: item.name,
            language: currentLang,
            hasCurrentDescription: !!currentDescription
          }
        });

        const translated = await translate();
        if (translated) {
          // Mettre à jour les descriptions de l'item
          item.descriptions[currentLang] = translated;

          logger.debug('Translation completed', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              translatedLength: translated.length
            }
          });
        }
      }
    };

    checkAndTranslate();
  }, [item.descriptions, i18n.language, translate, item.id, item.name]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const renderContent = (item: EventItem) => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      {/* Ligne 2: Place (sans icone) */}
      <div className="w-full">
        {item.place}
      </div>
      
      {/* Ligne 3: fulladress + telephone (sans icone) */}
      <div className="w-full">
        {item.fulladress && <span>{item.fulladress}</span>}
        {item.fulladress && item.telephone && <span> • </span>}
        {item.telephone && <span>{formatPhoneNumber(item.telephone)}</span>}
      </div>
      
      {/* Ligne 4: Distance + all three travel times */}
      <div className="flex flex-wrap gap-2">
        {item.distance && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{item.distance}km</span>
          </div>
        )}
        
        {/* Show walk time */}
        {item.timewalk && (
          <div className="flex items-center">
            <WalkIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timewalk, i18n.language)}</span>
          </div>
        )}
        
        {/* Show bike time */}
        {item.timebike && (
          <div className="flex items-center">
            <BikeIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timebike, i18n.language)}</span>
          </div>
        )}
        
        {/* Show drive time */}
        {item.timedrive && (
          <div className="flex items-center">
            <Car size={15} className="mr-1" fill="currentColor" />
            <span>{formatTravelTime(item.timedrive, i18n.language)}</span>
          </div>
        )}
      </div>
      
      {/* Ligne 5: Date + hour + price */}
      <div className="flex flex-wrap gap-2 items-center">
        {item.date && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              }).format(new Date(item.date))}
              {item.hour && item.end_time ? (
                ` ${formatTimeRange(item.hour, item.end_time, i18n.language)}`
              ) : item.hour && item.duration ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))} (${formatTravelTime(item.duration, i18n.language)})`
              ) : item.hour ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))}`
              ) : ''}
            </span>
          </div>
        )}
        
        {item.price && (
          <div 
            className="flex items-center"
            style={{ color: config['major-color'] }}
          >
            <Tag size={15} className="mr-1" />
            <span>{item.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingButton = (item: EventItem) => {
    if (!item.booking) return null;
    
    return (
      <a
        href={item.booking}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          analyticsService.trackItemAction(
            tableId,
            item.id,
            'booking_click',
            'event',
            {
              item_name: item.name,
              category: item.category,
              type: item.type,
              url: item.booking,
              section_name: section.names.fr || 'Unknown Section',
              section_type: 'event-api'
            }
          );
        }}
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-2"
        style={{ backgroundColor: config['major-color'] }}
      >
        <BookIcon size={18} className="mr-2" />
        {getTranslatedText('btn_booking')}
      </a>
    );
  };

  const handleFavoriteToggle = async (newState: boolean) => {
    try {
      const favoriteId = item.source === 'ticketmaster' ? `ticketmaster_${item.eventId}` : item.id;
      
      if (newState) {
        favoritesService.addFavorite({
          id: favoriteId,
          sourceType: 'event-api',
          data: {
            ...item,
            image: item.image // Ensure we're using the correct image URL
          },
          metadata: {
            sourceSection: section,
            sectionSlug: section.names.fr,
            sectionNames: section.names,
            item_id: item.id,
            section_id: section.id,
            source_hash: item.source === 'ticketmaster' ? item.description : undefined
          }
        });
        
        analyticsService.trackItemAction(
          section.id,
          item.id,
          'add_favorite',
          'event',
          {
            item_name: item.name,
            category: item.category,
            type: item.type,
            section_name: section.names.fr || 'Unknown Section',
            section_type: 'event-api'
          }
        );
      } else {
        favoritesService.removeFavorite(favoriteId);
        
        analyticsService.trackItemAction(
          section.id,
          item.id,
          'remove_favorite',
          'event',
          {
            item_name: item.name,
            category: item.category,
            type: item.type,
            section_name: section.names.fr || 'Unknown Section',
            section_type: 'event-api'
          }
        );
      }
      setIsFavorite(newState);
    } catch (error) {
      logger.error('Error toggling favorite', {
        category: 'Favorites',
        data: { error, itemId: item.id }
      });
    }
  };

  // Construire l'URL d'itinéraire depuis l'hôtel
  const getHotelItineraryUrl = (item: EventItem): string => {
    if (!item.fulladress || !config.fulladress) return '';

    // Encoder les adresses pour l'URL
    const origin = encodeURIComponent(config.fulladress);
    const destination = encodeURIComponent(item.fulladress);

    // Construire l'URL avec les coordonnées de l'hôtel comme origine
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  };

  // Obtenir la description à afficher
  const getDescription = () => {
    const currentLang = i18n.language;
    
    // Si une traduction existe déjà dans les descriptions, l'utiliser
    if (item.descriptions[currentLang]) {
      return item.descriptions[currentLang];
    }
    
    // Fallback sur la description par défaut
    return item.description;
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={getTranslatedCategory}
      getTranslatedText={getTranslatedText}
      itineraryUrl={getHotelItineraryUrl(item)}
      behavior={behavior}
      showIndex={showIndex}
      place={place}
      context={{
        translationTableId: context?.translationTableId || tableId,
        sectionType: 'event-api',
        source_hash: item.source === 'ticketmaster' ? item.description : undefined
      }}
      description={getDescription()}
      translating={translating}
      translationError={translationError}
      otherDates={otherDates}
      onOtherDateClick={onItemChange}
      onShowPlace={onShowPlace}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
    />
  );
};

export default DetailCard;